// import styles
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-transitions.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-thumbnail.css";

import ZoomInIcon from "@src/app/assets/icons/zoom-in.svg?react";
import ZoomOutIcon from "@src/app/assets/icons/zoom-out.svg?react";
import Button from "@src/app/components/Button/Button";
import cx from "classnames";
// import plugins
// @ts-expect-error polyfill
import lgZoom from "lightgallery/plugins/zoom/lg-zoom.umd.js";
// @ts-expect-error polyfill
import LightGallery from "lightgallery/react/Lightgallery.umd.js";
import { type ReactElement, useRef, useState } from "react";
import ReactDOM from "react-dom/client";

interface LightGalleryInstance {
  zoomIn?: () => void;
  zoomOut?: () => void;
}

export default function ComicLightbox({
  images,
}: {
  images: { src: string; alt: string }[];
}): ReactElement {
  const lgInstanceRef = useRef<LightGalleryInstance | null>(null);
  const hasInjectedZoomRef = useRef(false);
  const [comicLightBoxInit, setComicLightBoxInit] = useState(false);

  const onInit = (detail: { instance: LightGalleryInstance }) => {
    lgInstanceRef.current = detail.instance;
    setComicLightBoxInit(true);
    console.log("ComicLightBox init");
  };

  // Hide default buttons
  const hideDefaultZoomButtons = () => {
    const zoomIn = document.querySelector<HTMLElement>(".lg-zoom-in");
    const zoomOut = document.querySelector<HTMLElement>(".lg-zoom-out");

    [zoomIn, zoomOut].forEach((btn) => {
      if (btn) {
        btn.style.opacity = "0";
        btn.style.pointerEvents = "none";
      }
    });
  };

  // Trigger custom click
  const triggerClick = (selector: string) => {
    const btn = document.querySelector<HTMLElement>(selector);
    btn?.click();
  };
  const handleZoomIn = () => {
    triggerClick(".lg-zoom-in");
  };
  const handleZoomOut = () => {
    triggerClick(".lg-zoom-out");
  };

  // Inject custom buttons when gallery opens
  const onAfterOpen = () => {
    const observer = new MutationObserver(() => {
      // HARD GUARD (runs immediately)
      if (hasInjectedZoomRef.current) return;

      const container = document.querySelector(".lg-content");
      if (!container || !lgInstanceRef.current) return;

      // Lock immediately (before rAF)
      hasInjectedZoomRef.current = true;

      observer.disconnect();

      requestAnimationFrame(() => {
        hideDefaultZoomButtons();

        const isDesktopView = window.matchMedia("(min-width: 992px)").matches;
        const wrapper = document.createElement("div");
        wrapper.className = "lg-custom-zoom";

        ReactDOM.createRoot(wrapper).render(
          <>
            <Button
              className="lg-custom-zoom-in"
              variant="tertiary"
              size={isDesktopView ? "large" : "medium"}
              aria-label="Zoom in"
              onClick={handleZoomIn}
              icon={<ZoomInIcon />}
            />
            <Button
              className="lg-custom-zoom-out"
              variant="tertiary"
              size={isDesktopView ? "large" : "medium"}
              aria-label="Zoom out"
              onClick={handleZoomOut}
              icon={<ZoomOutIcon />}
            />
          </>,
        );

        container.appendChild(wrapper);
        console.log("Custom zoom buttons injected");
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };
  return (
    <div id="comicLightbox">
      <LightGallery
        onInit={onInit}
        onAfterOpen={onAfterOpen}
        plugins={[lgZoom]} //lgHash
        actualSize={false}
        counter={false}
        customSlideName={true}
        download={false}
        fullScreen={false}
        getCaptionFromTitleOrAlt={false}
        mobileSettings={{ showCloseIcon: true, fullScreen: false }}
        mode="lg-slide"
        showZoomInOutIcons={true}
        speed={300}
      >
        {images.map((item, index) => (
          <img
            className={cx(
              "mb-4",
              comicLightBoxInit ? "cursor-pointer" : "pointer-none",
            )}
            src={item.src}
            alt={item.alt}
            loading="lazy"
            key={index}
          />
        ))}
      </LightGallery>
    </div>
  );
}
